import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { baseUrl } from "../index";
import axios from "axios";
import { getAuthToken } from "../../utils/utils";

export const fetchMonthlyIncome = createAsyncThunk(
  "profitLoss/fetchMonthlyIncome",
  async ({ userId, year, type }, thunkAPI) => {
    try {
      const token = getAuthToken();

      const response = await axios.get(`${baseUrl}/month-wise-income`, {
        params: { userId, year, type }, // Changed from data to params
        headers: {
          Authorization: `${token}`, // Pass the token here
          "Content-Type": "application/json", // Make sure the content type is correct
        },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const fetchMonthlyDropouts = createAsyncThunk(
  "profitLoss/fetchMonthlyDropouts",
  async ({ userId, year },thunkAPI) => {
    try{
      const token = getAuthToken();

    const response = await axios.get(`${baseUrl}/month-wise-dropouts`, {
      params: { userId, year }, // Changed from data to params
      headers: {
        Authorization: `${token}`, // Pass the token here
        "Content-Type": "application/json", // Make sure the content type is correct
      },
    });
    return response.data;
    }
    catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const calculateNetRevenue = createAsyncThunk(
  "profitLoss/calculateNetRevenue",
  async ({ userId, year },thunkAPI) => {
    try{
      const token = getAuthToken();

    const response = await axios.post(
      `${baseUrl}/month-wise-net-revenue`,
      {
        userId,
        year,
      },
      {
        headers: {
          Authorization: `${token}`, // Pass the token here
          "Content-Type": "application/json", // Make sure the content type is correct
        },
      }
    );
    return response.data;
    }
    catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const addExpense = createAsyncThunk(
  "profitLoss/addExpense",
  async (expenseData, thunkAPI) => {
    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${baseUrl}/addexpensepermonth`,
        expenseData,
        {
          headers: {
            Authorization: `${token}`, // Pass the token here
            "Content-Type": "application/json", // Make sure the content type is correct
          },
        }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const createNewExpense = createAsyncThunk(
  "profitLoss/createNewExpense",
  async ({ userId, year }, thunkAPI) => {
    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${baseUrl}/create-expense`,
        {
          userId,
          year,
        },
        {
          headers: {
            Authorization: `${token}`, // Pass the token here
            "Content-Type": "application/json", // Make sure the content type is correct
          },
        }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const calculateNetIncome = createAsyncThunk(
  "profitLoss/calculateNetIncome",
  async ({ userId, year }, thunkAPI) => {
    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${baseUrl}/month-wise-net-income`,
        {
          userId,
          year,
        },
        {
          headers: {
            Authorization: `${token}`, // Pass the token here
            "Content-Type": "application/json", // Make sure the content type is correct
          },
        }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const deleteExpense = createAsyncThunk(
  "profitLoss/deleteExpense",
  async ({ userId, expenseId }, thunkAPI) => {
    try {
      const token = getAuthToken();
      const response = await axios.delete(
        `${baseUrl}/delete-expense/${expenseId}`,
        {
          headers: {
            Authorization: `${token}`, // Pass the token here
            "Content-Type": "application/json", // Make sure the content type is correct
          },
        }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const fetchExpenses = createAsyncThunk(
  "profitLoss/fetchExpenses",
  async ({ userId, year }, thunkAPI) => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${baseUrl}/expenses`, {
        params: { userId, year },
        headers: {
          Authorization: `${token}`, // Pass the token here
          "Content-Type": "application/json", // Make sure the content type is correct
        },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const fetchClinicSalesRevenue = createAsyncThunk(
  "profitLoss/fetchClinicSalesRevenue",
  async ({ userId, year }, thunkAPI) => {
    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${baseUrl}/month-wise-actual-clinicsales-revenue`,
        {
          params: { userId, year },
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const calculateTotalActualRevenue = createAsyncThunk(
  "profitLoss/calculateTotalActualRevenue",
  async ({ userId, year }, thunkAPI) => {
    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${baseUrl}/month-wise-total-actual-revenue`,
        { userId, year },
        {
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

const profitLossSlice = createSlice({
  name: "profitLoss",
  initialState: {
    monthlyIncome: {
      projected: null,
      actual: null,
    },
    monthlyDropouts: null,
    netRevenue: null,
    expenses: [],
    clinicSalesRevenue: null,
    totalActualRevenue: null,
    netIncome: null,
    loading: false,
    error: null,
  },
  reducers: {
    updateExpenses: (state, action) => {
      state.expenses = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Monthly Income Cases
      .addCase(fetchMonthlyIncome.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMonthlyIncome.fulfilled, (state, action) => {
        state.loading = false;
        // Handle different types of income (projected vs actual)
        if (action.meta.arg.type === "Projected") {
          state.monthlyIncome.projected = action.payload.monthlyRevenue;
        } else if (action.meta.arg.type === "Actual") {
          state.monthlyIncome.actual = action.payload.monthlyRevenue;
        }
      })
      .addCase(fetchMonthlyIncome.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Monthly Dropouts Cases
      .addCase(fetchMonthlyDropouts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMonthlyDropouts.fulfilled, (state, action) => {
        state.loading = false;
        state.monthlyDropouts = action.payload.monthlyDropouts;
      })
      .addCase(fetchMonthlyDropouts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Net Revenue Cases
      .addCase(calculateNetRevenue.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(calculateNetRevenue.fulfilled, (state, action) => {
        state.loading = false;
        state.netRevenue = action.payload.netRevenue;
      })
      .addCase(calculateNetRevenue.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Add Expense Cases
      .addCase(addExpense.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addExpense.fulfilled, (state, action) => {
        state.loading = false;
        // Update existing expense or add new one
        const existingIndex = state.expenses.findIndex(
          (exp) => exp._id === action.payload.data._id
        );
        if (existingIndex !== -1) {
          state.expenses[existingIndex] = action.payload.data;
        } else {
          state.expenses.push(action.payload.data);
        }
      })
      .addCase(addExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Create New Expense Cases
      .addCase(createNewExpense.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNewExpense.fulfilled, (state, action) => {
        state.loading = false;
        state.expenses.push(action.payload.data);
      })
      .addCase(createNewExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Net Income Cases
      .addCase(calculateNetIncome.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(calculateNetIncome.fulfilled, (state, action) => {
        state.loading = false;
        state.netIncome = action.payload.netIncome;
      })
      .addCase(calculateNetIncome.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Delete Expense Cases
      .addCase(deleteExpense.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteExpense.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.expenses = state.expenses.filter(
            (expense) => expense._id !== action.payload.expenseId
          );
        }
      })
      .addCase(deleteExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Fetch Expenses Cases
      .addCase(fetchExpenses.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchExpenses.fulfilled, (state, action) => {
        state.loading = false;
        state.expenses = action.payload.data;
      })
      .addCase(fetchExpenses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      .addCase(fetchClinicSalesRevenue.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClinicSalesRevenue.fulfilled, (state, action) => {
        state.loading = false;
        state.clinicSalesRevenue = action.payload.monthWiseRevenues;
      })
      .addCase(fetchClinicSalesRevenue.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Total Actual Revenue Cases
      .addCase(calculateTotalActualRevenue.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(calculateTotalActualRevenue.fulfilled, (state, action) => {
        state.loading = false;
        state.totalActualRevenue = action.payload.totalActualRevenue;
      })
      .addCase(calculateTotalActualRevenue.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { updateExpenses } = profitLossSlice.actions;

export default profitLossSlice.reducer;
